import { useCallback, useEffect } from "react";
import { useParams } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { getOrCreateDeviceId } from "@/lib/deviceId";
import { getToolById } from "@/lib/tools-data";
import { isPremiumApp } from "@shared/tiers";
import { usePaidSuite } from "@/components/EventSuiteAccess";
import { beginLog, confirmAtEdge, endLog, ensureUsage, isToolSessionCounted, noteExtraUseSpent, requestToolSessionUse, requestUse, usageScope, type UseDecision } from "@/lib/usageGate";
import { commitExport, planExport } from "@/lib/exportCharge";

/**
 * Shared plumbing for both gate hooks (useLogUsageOnAction for button
 * actions, useLogToolUsage for live tools): the tool's id and category,
 * the cached-usage scope, a background prefetch of the usage count so the
 * first click already has an answer, and `tryUse` -- the decision itself.
 * Logging goes through the tRPC client directly (not a useMutation) so a
 * log started just before a page change still settles and updates the
 * shared count instead of being dropped with the component.
 */
export function useUsageGate() {
  const params = useParams<{ id?: string }>();
  const toolId = params.id ?? "unknown";
  const category = getToolById(toolId)?.category ?? "unknown";
  const { isAuthenticated } = useAuth();
  const utils = trpc.useUtils();
  const deviceId = isAuthenticated ? undefined : getOrCreateDeviceId();
  const scope = usageScope(isAuthenticated, deviceId, isPremiumApp(toolId));
  const known = toolId !== "unknown" && category !== "unknown";
  // Inside the paid Event Suite the daily trial count does not apply.
  const paid = usePaidSuite();

  const fetcher = useCallback(() => utils.client.usage.check.query({ toolId, deviceId }), [utils, toolId, deviceId]);

  useEffect(() => {
    if (known) ensureUsage(scope, fetcher);
  }, [known, scope, fetcher]);

  const logOne = useCallback(() => {
    beginLog();
    const done = () => endLog(scope, fetcher, () => void utils.usage.check.invalidate());
    utils.client.usage.log.mutate({ toolId, category, deviceId }).then(done, done);
  }, [scope, fetcher, utils, toolId, category, deviceId]);

  const tryUse = useCallback(
    async (): Promise<UseDecision> => {
      if (paid) return "allowed";
      if (!isToolSessionCounted(toolId)) await confirmAtEdge(scope, fetcher);
      return requestToolSessionUse(toolId, scope, fetcher, logOne);
    },
    [paid, scope, fetcher, toolId, logOne],
  );

  /**
   * Like tryUse, but one use per distinct export (see lib/exportCharge.ts): the same key again is free, the
   * first export of the visit takes the visit's use, and an export with a new key takes another use.
   */
  const tryUseExport = useCallback(
    async (key: string): Promise<UseDecision> => {
      if (paid) return "allowed";
      const plan = planExport(toolId, key);
      if (plan === "free") return "allowed";
      await confirmAtEdge(scope, fetcher);
      const d = plan === "first" ? await requestToolSessionUse(toolId, scope, fetcher, logOne) : await requestUse(scope, fetcher, logOne);
      if (d === "allowed") {
        commitExport(toolId, key);
        if (plan !== "first") noteExtraUseSpent(toolId);
      }
      return d;
    },
    [paid, toolId, scope, fetcher, logOne],
  );

  return { known, scope, fetcher, tryUse, tryUseExport, paid, toolId };
}
